import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { toast } from "react-toastify";
import PlatformActionModal from "./PlatformActionModal";
import {
  activatePlatformBusiness,
  banPlatformBusiness,
  suspendPlatformBusiness,
} from "./platformAdminApi";
import { PLATFORM_BAN_REASONS, PLATFORM_SUSPEND_REASONS } from "../../constants/platformAdminDummyData";

const PlatformAdminContext = createContext(null);

export function getActionConfig(type, business) {
  if (!business) return null;
  const name = business.name || "this business";
  switch (type) {
    case "suspend":
      return {
        tone: "danger",
        title: "Suspend business",
        description: `All agents of ${name} will be signed out and the chat widget will stop accepting new chats.`,
        select: {
          label: "Reason",
          options: PLATFORM_SUSPEND_REASONS.map((r) => ({ value: r, label: r })),
          defaultValue: PLATFORM_SUSPEND_REASONS[0],
        },
        confirmLabel: "Suspend",
      };
    case "ban":
      return {
        tone: "danger",
        title: "Ban business",
        description: `${name} will be permanently banned from the platform until manually activated again.`,
        select: {
          label: "Reason",
          options: PLATFORM_BAN_REASONS.map((r) => ({ value: r, label: r })),
          defaultValue: PLATFORM_BAN_REASONS[0],
        },
        confirmLabel: "Ban",
      };
    case "activate":
      return {
        tone: "info",
        title: "Activate business",
        description: `${name} will regain access immediately and agents will be able to sign in again.`,
        confirmLabel: "Activate",
      };
    default:
      return null;
  }
}

export function getAvailableActions(business) {
  if (!business) return [];
  const status = (business.status || "").toLowerCase();
  const actions = [];
  if (status === "active") {
    actions.push({ key: "suspend", label: "Suspend", danger: true });
    actions.push({ key: "ban", label: "Ban", danger: true });
  } else if (status === "suspended") {
    actions.push({ key: "activate", label: "Activate" });
    actions.push({ key: "ban", label: "Ban", danger: true });
  } else if (status === "banned") {
    actions.push({ key: "activate", label: "Activate" });
  } else {
    actions.push({ key: "activate", label: "Activate" });
    actions.push({ key: "suspend", label: "Suspend", danger: true });
    actions.push({ key: "ban", label: "Ban", danger: true });
  }
  return actions;
}

export function PlatformAdminProvider({ children }) {
  const [pending, setPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const requestAction = useCallback((type, business, options = {}) => {
    setPending({ type, business, onDone: options.onDone });
  }, []);

  const handleConfirm = async ({ selectValue }) => {
    if (!pending || submitting) return;
    const { type, business, onDone } = pending;
    const id = business.id || business._id;
    setSubmitting(true);
    let result;
    if (type === "suspend") {
      result = await suspendPlatformBusiness(id, { reason: selectValue });
    } else if (type === "ban") {
      result = await banPlatformBusiness(id, { reason: selectValue });
    } else if (type === "activate") {
      result = await activatePlatformBusiness(id);
    }
    setSubmitting(false);

    if (result?.success) {
      toast.success(result.message || `${business.name} updated`);
      setPending(null);
      onDone?.(type, result);
    } else {
      toast.error(result?.message || "Action failed. Please try again.");
    }
  };

  const value = useMemo(() => ({ requestAction }), [requestAction]);

  const pendingConfig = useMemo(
    () => (pending ? getActionConfig(pending.type, pending.business) : null),
    [pending]
  );

  return (
    <PlatformAdminContext.Provider value={value}>
      {children}
      <PlatformActionModal
        open={Boolean(pending)}
        config={pendingConfig}
        onClose={() => !submitting && setPending(null)}
        onConfirm={handleConfirm}
      />
    </PlatformAdminContext.Provider>
  );
}

export function usePlatformAdmin() {
  const ctx = useContext(PlatformAdminContext);
  if (!ctx) throw new Error("usePlatformAdmin must be used inside PlatformAdminProvider");
  return ctx;
}
